/** @jsxImportSource @emotion/react */
import { jsx } from '@emotion/react'
import { shallowEqual } from 'react-redux'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Box, Button, IconButton, Input, LinearProgress } from '@material-ui/core'
import VerticalAlignBottomIcon from '@material-ui/icons/VerticalAlignBottom'
import { DetailSliderContent } from 'components/partials'
import { AlertChatEvents } from './AlertChatEvents'
import CondensedInfoGadget from 'components/partials/CondensedInfoGadget'
import SolveAlertDialog from 'components/partials/SolveAlertDialog'
import { ActionButton, ConditionalRender } from 'components/widgets'
import {
  ALL_ORGS_SELECTED,
  BRAND_COLOR,
  ERROR_COLOR,
  GRAY3_COLOR,
  HealthLabelsKeys,
  JWT_TOKEN,
  TEXTFIELD_LABEL_COLOR,
  getAppColors,
} from 'lib/constants'
import { AlertEvent, AlertSession, trans, TranslationGroup, TranslationKey } from 'lib/types'
import { AlertEvents, NavState, Orgs, AlertSessions } from 'models'
import * as utils from 'components/partial-pages/AlertsView/utils'
import { DetailRow } from './utils'
import {
  actionRowStyles,
  alertEventsWrapperStyles,
  alertEventCountStyles,
  alertHistoryTitleStyles,
  detailHeadingStyles,
  detailStyles,
  linearProgressStyles,
} from 'styles'
import { getAlertTimes, formatLocalTime } from 'lib/utils'
import { v4 as getUuidv4 } from 'uuid'
import { getUnixTime } from 'date-fns'
import jwt_decode from 'jwt-decode'
import { isAdmin, isDispatcher, isFleetVoiceOrg, isPortalUser, isResponder, isSuperAdmin } from 'lib/utils/auth'
import { ACTION_TYPE } from 'lib/constants/keys'
import { HealthIcon, HealthStatus } from 'components/widgets/HealthIcon'
import LocationMap from 'components/partial-pages/AlertsView/Partials/LocationMap'
import VenueDrawer from 'components/partial-pages/AlertsView/Partials/VenueDrawer'
import { useGlobal } from 'contexts/GlobalProvider'
import { FormControlLabel } from '@material-ui/core'
import { ToggleSwitch, Toggle, SwitchLabel } from 'components/partials/MappedInIntegrationForm'
import { getFloors } from 'models/api/floors'
import styled from '@emotion/styled'
import { Select, MenuItem, FormControl } from '@material-ui/core'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import { reload as reloadAlertSessions } from 'models/AlertSessions'
import { FingerprintError } from 'lib/types/fingerprints'
import { fingerprintingFlag } from 'lib/utils/featureFlags'

import { useVoice } from 'contexts/VoiceProvider'
import type { Channel } from 'lib/types/channels'
import PTTButton from 'components/partials/PTTButton'

const StyledFormControl = styled(FormControl)({
  background: getAppColors('--color-gray-4'),
  borderRadius: '10px',
  textAlign: 'center',
  width: '250px',
})

export const AlertDetailDrawer = ({
  alertSession: _alert,
  close: handleDrawerClose,
}: {
  alertSession: AlertSession
  close: Function
}) => {
  const alertSessionsById = AlertSessions.use(({ alertSessionsById }) => alertSessionsById)
  const alertSession = alertSessionsById[_alert.id] || _alert

  const [mapVisible, setMapVisible] = useState(true)
  const [showDialog, setShowDialog] = useState(false)
  const [showArrivedDialog, setShowArrivedDialog] = useState(false)
  const [responderNames, setResponderNames] = useState<string[]>([])
  const [filteredAlertEvents, setFilteredAlertEvents] = useState<AlertEvent[]>([])
  const [map, setMap] = useState<string | null>(null)
  const [mapAvailable, setMapAvailable] = useState(!!map)
  const [previousMap, setPreviousMap] = useState<string | null>(null)
  const [fingerprintMode, setFingerprintMode] = useState(false)
  const [showAllFingerprints, setShowAllFingerprints] = useState(false)
  const [floors, setFloors] = useState<any[]>([])
  const [selectedFloor, setSelectedFloor] = useState<any | null>(null)
  const [canUseMappedin, setCanUseMappedin] = useState(true)

  const {
    selectedOrg,
    selectedOrgId,
    currentUserV5,
    licenses,
    licensesLoading,
    licensesError,
    selectOrg,
    findVoiceOrgId,
  } = useGlobal()
  const {
    zelloState,
    enterExclusiveChannel,
    exitExclusiveChannel,
    exclusiveChannelName,
    pttStart,
    pttStop,
    voiceReady,
    isChannelReady,
    reconnect,
    isKicked,
  } = useVoice()

  const orgsById = Orgs.use(({ orgsById }) => orgsById)
  const property = orgsById[alertSession.orgId]
  const { alertEvents, loading: alertEventsLoading } = AlertEvents.use(({ alertEvents, loading }) => {
    return { alertEvents, loading }
  }, shallowEqual)

  // The events store is global and only ever holds one alert at a time. Scope it
  // to this drawer's alert as a safety net so a transient/mismatched store can
  // never render another alert's events in this chat.
  const eventsForThisAlert = useMemo(
    () => alertEvents.filter(e => e.attributes?.alertSessionId === alertSession.id),
    [alertEvents, alertSession.id],
  )

  const [comment, setComment] = useState('')
  const [scrolledToBottom, setScrolledToBottom] = useState(false)
  const [movingThroughHistory, setMovingThroughHistory] = useState(false)
  const chatScrollRef = useRef<HTMLDivElement>(null)
  const chatBottomRef = useRef<HTMLDivElement>(null)

  const scrollChatToBottom = useCallback(() => {
    chatBottomRef.current?.scrollIntoView({ block: 'end' })
  }, [])
  const arrivedDisabled = false
  const translation: TranslationGroup = trans.merge(TranslationKey.ALERT_DRAWER)

  const lastBeacons = alertSession.lastBeacons ?? []
  const lastFingerprints: FingerprintError[] = alertSession.lastFingerprints ?? []

  // The alert source's own last reported location, never the resolver's: the chat
  // alone doesn't say where the alert is coming from, so it stays pinned in view.
  const lastLocation = alertSession.lastLocation
  const lastLocationText = `${translation.room} ${lastLocation?.room || translation.na}, ${translation.floor} ${
    lastLocation?.floor || translation.na
  }`

  const statusLc = String(alertSession?.status || '').toLowerCase()
  const isClosed = statusLc === 'closed' || statusLc === 'resolved'
  const isActive = !isClosed
  // A voice client exists for the org: Zello credentials, or fleet.
  const hasZello = !!selectedOrg?.zelloUsername || isFleetVoiceOrg(selectedOrg)

  const hasPostedEnrollRef = useRef(false)
  const hasPostedDepartRef = useRef(false)

  const norm = (v: any) =>
    String(v ?? '')
      .trim()
      .toLowerCase()

  const isActiveLicense = (l: any) => {
    const status = norm(l.status)
    if (typeof l.active === 'boolean') return l.active
    if (typeof l.isActive === 'boolean') return l.isActive
    if (status) return status === 'active' || status === 'enabled'
    return true
  }

  const isVoiceLicense = (l: any) => {
    const t = norm(l.type ?? l.licenseType ?? l.product ?? l.name ?? l.slug)
    return t.includes('voice') || t.includes('zello')
  }

  const isAssignedToUser = (l: any, user: any) => {
    const userId = user?.id
    const userEmail = norm(user?.email)

    const assignedId =
      l.userId ?? l.assignedToUserId ?? l.assigneeId ?? l.user?.id ?? l.assignee?.id ?? l.assignedUserId

    const assignedEmail = norm(
      l.userEmail ?? l.assigneeEmail ?? l.user?.email ?? l.assignee?.email ?? l.assignedUserEmail,
    )

    return (
      (userId && assignedId && String(assignedId) === String(userId)) ||
      (userEmail && assignedEmail && assignedEmail === userEmail)
    )
  }

  const hasVoiceLicense =
    !!currentUserV5?.id &&
    Array.isArray(licenses) &&
    licenses.some(l => isActiveLicense(l) && isVoiceLicense(l) && isAssignedToUser(l, currentUserV5))

  const voiceLicenseKnown = !licensesLoading && !licensesError
  const voiceAllowed = voiceLicenseKnown && hasVoiceLicense

  const voiceGateTooltip = licensesLoading
    ? 'Checking voice license…'
    : licensesError
    ? 'Unable to verify voice license'
    : hasVoiceLicense
    ? undefined
    : 'Voice License Required'

  const wasActiveRef = useRef<boolean | null>(null)
  const hasAttemptedJoinRef = useRef(false)

  const { startTime, endTime } = getAlertTimes(alertSession)

  const getCurrentUserId = () => {
    if (currentUserV5?.id) return currentUserV5.id

    try {
      const token = localStorage.getItem(JWT_TOKEN) || ''
      if (!token) return null
      const decoded = jwt_decode(token) as any
      return JSON.parse(decoded.sub).id
    } catch (err) {
      console.error('Unable to resolve current user id for alert presence event', err)
      return null
    }
  }

  const postDrawerPresenceEvent = useCallback(
    (action: 'enroll' | 'depart') => {
      const userId = getCurrentUserId()
      if (!userId || !alertSession?.id || !alertSession?.orgId) return

      const alertEvent = {
        alertEvent: {
          id: getUuidv4(),
          alertSessionId: alertSession.id,
          userId,
          orgId: alertSession.orgId,
          timestamp: getUnixTime(new Date()),
          message: null,
          action,
          beacons: [],
          role: 'responder',
          location: {},
        },
      }

      try {
        AlertEvents.createAlertEvent(alertEvent)
      } catch (err) {
        console.error(`Failed to post alert detail drawer ${action} event`, err)
      }
    },
    [alertSession?.id, alertSession?.orgId, currentUserV5?.id],
  )

  useEffect(() => {
    hasPostedEnrollRef.current = false
    hasPostedDepartRef.current = false
  }, [alertSession?.id])

  useEffect(() => {
    if (hasPostedEnrollRef.current) return

    postDrawerPresenceEvent('enroll')
    hasPostedEnrollRef.current = true

    return () => {
      if (!hasPostedDepartRef.current) {
        postDrawerPresenceEvent('depart')
        hasPostedDepartRef.current = true
      }
    }
  }, [postDrawerPresenceEvent])

  useEffect(() => {
    const prev = wasActiveRef.current
    if (prev === null) {
      wasActiveRef.current = isActive
      return
    }

    if (prev === true && isActive === false) {
      void exitExclusiveChannel()
    }

    wasActiveRef.current = isActive
  }, [isActive, exitExclusiveChannel])

  useEffect(() => {
    NavState.setSelectedAlertSessionId(alertSession.id)
  }, [alertSession.id])

  // Clear the global selection when the drawer unmounts (covers every close
  // path). This tears down the alert-event Pusher subscription and lets the
  // same alert reload cleanly if re-opened. Switching directly between alerts
  // updates props without unmounting, so this does not fire on alert change.
  useEffect(() => {
    return () => {
      NavState.setSelectedAlertSessionId(undefined)
    }
  }, [])

  const exitExclusiveChannelRef = useRef(exitExclusiveChannel)

  useEffect(() => {
    exitExclusiveChannelRef.current = exitExclusiveChannel
  }, [exitExclusiveChannel])

  useEffect(() => {
    return () => {
      void exitExclusiveChannelRef.current()
    }
  }, [])

  useEffect(() => {
    hasAttemptedJoinRef.current = false
  }, [alertSession?.id])

  // Voice (Zello subdomain, licenses, channel org) runs off the sidebar org, so
  // under All Properties the alert has no PTT. Switch to the org that owns this
  // alert's voice — its own, or its group's — once per alert, so switching back
  // to All while the drawer is open is respected. Super admins never hold the
  // voice license the switch would be for.
  const voiceOrgSwitchedForRef = useRef<string | null>(null)
  // Read when the lookup resolves: the user may have picked an org meanwhile,
  // or closed the drawer.
  const selectedOrgIdRef = useRef(selectedOrgId)
  selectedOrgIdRef.current = selectedOrgId
  const mountedRef = useRef(true)
  useEffect(
    () => () => {
      mountedRef.current = false
    },
    [],
  )
  useEffect(() => {
    if (!isActive || !alertSession?.id || selectedOrgId !== ALL_ORGS_SELECTED || isSuperAdmin()) return
    if (voiceOrgSwitchedForRef.current === alertSession.id) return
    voiceOrgSwitchedForRef.current = alertSession.id

    findVoiceOrgId(alertSession.orgId).then(orgId => {
      if (orgId && mountedRef.current && selectedOrgIdRef.current === ALL_ORGS_SELECTED) selectOrg(orgId)
    })
  }, [isActive, alertSession?.id, alertSession?.orgId, selectedOrgId, findVoiceOrgId, selectOrg])

  useEffect(() => {
    if (hasAttemptedJoinRef.current) return

    if (!isActive) return
    if (!alertSession?.id || !selectedOrgId || !currentUserV5?.id) return
    if (!voiceReady) return
    if (!hasZello) return
    if (!voiceAllowed) return

    // ✅ NEW: do not connect unless the user has a voice license
    if (!voiceAllowed) return

    hasAttemptedJoinRef.current = true

    const ch: Channel = {
      id: alertSession.id,
      name: alertSession.id,
      orgId: selectedOrgId,
      health: 2,
    } as any

    ;(async () => {
      try {
        await enterExclusiveChannel(ch)
      } catch (err) {
        console.error('Auto-join alert channel failed', err)
        hasAttemptedJoinRef.current = false
      }
    })()
  }, [
    isActive,
    alertSession?.id,
    selectedOrgId,
    currentUserV5?.id,
    voiceReady,
    hasZello,
    voiceAllowed,
    enterExclusiveChannel,
  ])

  // Spec §158-160: start_stream isn't valid until Zello has emitted
  // `on_channel_status: online` for the channel — so treat the channel as
  // truly ready (and the PTT as truly enabled) only once isChannelReady is
  // true, not just on logon-success.
  const channelReady = isChannelReady(alertSession.id)
  const isConnectedToThisAlert =
    zelloState.status === 'connected' &&
    String(zelloState.channel || '') === String(alertSession.id || '') &&
    String(exclusiveChannelName || '') === String(alertSession.id || '') &&
    channelReady

  const alertConnectionFailed = Boolean(zelloState.connectionFailed && !channelReady)
  const pttDisabled = !voiceAllowed || !isConnectedToThisAlert
  const connectingUI = !alertConnectionFailed && voiceAllowed && !isConnectedToThisAlert && isActive && hasZello

  const handlePttStart = () => {
    if (pttDisabled) return
    pttStart()
  }

  const handlePttStop = () => {
    pttStop()
  }

  const handleCloseDrawer = () => {
    if (!hasPostedDepartRef.current) {
      postDrawerPresenceEvent('depart')
      hasPostedDepartRef.current = true
    }

    void exitExclusiveChannel()
    handleDrawerClose()
  }

  function handleScroll(e: any) {
    const atBottom = Math.abs(e.target.clientHeight - (e.target.scrollHeight - e.target.scrollTop)) <= 1
    setScrolledToBottom(atBottom)
    setMovingThroughHistory(!atBottom)
  }

  function getEventCount() {
    return filteredAlertEvents.length
  }

  const shouldShowAddComment =
    _alert.status !== HealthLabelsKeys.RESOLVED &&
    (isAdmin(_alert.orgId) || isResponder(_alert.orgId) || isPortalUser(_alert.orgId))

  useEffect(() => {
    if (fingerprintMode && alertSession?.orgId) {
      getFloors(alertSession.orgId).then(data => {
        if (Array.isArray(data)) {
          setFloors(data)
          const currentFloor = data.find(f => f.name === alertSession?.lastLocation?.floor)
          if (data.length > 0) setSelectedFloor(currentFloor)
        } else {
          console.warn('⚠️ getFloors did not return an array:', data)
          setFloors([])
        }
      })
    }
  }, [fingerprintMode, alertSession?.orgId])

  useEffect(() => {
    const responders = eventsForThisAlert.reduce((acc: string[], ae: AlertEvent) => {
      const asn = ae.attributes.alertSourceName
      if (ae.attributes.role !== 'initiator' && !ae.attributes.location?.integration && asn && !acc.includes(asn)) {
        acc.push(asn)
      }
      return acc
    }, [])
    setResponderNames(responders)
    setScrolledToBottom(false)

    const filtered = eventsForThisAlert.filter(event => {
      if (
        (event.attributes.action === null &&
          event.attributes?.map !== null &&
          event.attributes?.beacons?.length === 0 &&
          event.attributes?.location?.integration === null) ||
        (event.attributes.action === null &&
          event.attributes?.message === null &&
          event.attributes?.beacons?.length === 0 &&
          event.attributes?.location?.integration === undefined)
      ) {
        return false
      }
      return true
    })
    setFilteredAlertEvents(filtered)
  }, [eventsForThisAlert])

  useEffect(() => {
    if (!movingThroughHistory) {
      scrollChatToBottom()
    }
  }, [filteredAlertEvents])

  const sendComment = () => {
    if (!comment) return
    const token = localStorage.getItem(JWT_TOKEN) || ''
    const decoded = jwt_decode(token) as any
    const userId = JSON.parse(decoded.sub).id

    const alertEvent = {
      alertEvent: {
        id: getUuidv4(),
        alertSessionId: _alert.id,
        userId,
        orgId: _alert.orgId,
        timestamp: getUnixTime(new Date()),
        message: comment,
        beacons: [],
        role: 'responder',
        location: {},
      },
    }

    AlertEvents.createAlertEvent(alertEvent)
    setComment('')
  }

  function scrollToBottomHandler() {
    setScrolledToBottom(false)
    setMovingThroughHistory(false)
    scrollChatToBottom()
  }

  const getHealthStatus = () => {
    const health = utils.getAlertHealth(alertSession)
    let status = HealthStatus.UNKNOWN
    switch (health) {
      case HealthLabelsKeys.ACTIVE_NO_RESPONSE:
        status = HealthStatus.ACTIVE_NO_RESPONSE
        break
      case HealthLabelsKeys.ACTIVE_IN_PROGRESS:
        status = HealthStatus.MODERATE
        break
      case HealthLabelsKeys.RESOLVED:
        status = HealthStatus.HEALTHY
        break
    }
    return <HealthIcon label={translation[health]} status={status} />
  }

  const getMap = (aes: AlertEvent[]) => {
    return aes.filter(ae => ae?.attributes?.map !== null).pop()?.attributes?.map || null
  }

  useEffect(() => {
    let interval: any = null
    if (fingerprintMode && alertSession?.id) {
      interval = setInterval(() => {
        console.log('~🔁 Reloading alertSessions for fingerprint mode...')
        reloadAlertSessions()
      }, 10000)
    }
    return () => {
      if (interval) clearInterval(interval)
    }
  }, [fingerprintMode, alertSession?.id])

  useEffect(() => {
    const newMap = getMap(eventsForThisAlert)
    map ? setPreviousMap(map) : setPreviousMap(newMap)
    setMap(newMap)
  }, [eventsForThisAlert])

  useEffect(() => {
    setMapAvailable(!!(map || previousMap))
  }, [map, previousMap])

  return (
    <Box
      id="AlertDetailDrawer"
      style={{
        height: '100%',
        width: '600px',
        backgroundColor: `${GRAY3_COLOR}`,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {selectedOrg?.mappedin?.isEnabled && canUseMappedin && !!alertSession?.lastLocation?.room ? (
        <VenueDrawer
          alertSession={alertSession}
          visible={mapVisible}
          mapAvailable={!!alertSession?.lastLocation?.room}
          setCanUseMappedin={setCanUseMappedin}
          map={map}
          previousMap={previousMap}
          fingerprintMode={fingerprintMode}
          showAllFingerprints={showAllFingerprints}
          selectedFloor={selectedFloor}
          lastBeacons={alertSession?.lastBeacons}
          lastFingerprints={alertSession?.lastFingerprints}
        />
      ) : (
        !alertEventsLoading && (
          <LocationMap visible={mapVisible} mapAvailable={mapAvailable} map={map} previousMap={previousMap} />
        )
      )}

      <DetailSliderContent title={translation.Alert_Detail} close={handleCloseDrawer}>
        <ul css={detailStyles}>
          <DetailRow title={translation.property} content={property?.name} />
          <DetailRow title={translation.property_id} content={alertSession.orgId} />
          <DetailRow title={translation.Status} content={getHealthStatus()} />
          <DetailRow title={translation.Start} content={formatLocalTime(startTime)} />
          <DetailRow title={translation.End} content={formatLocalTime(endTime, translation.ongoing) as string} />
          <DetailRow title={translation.Resolved_In} content={utils.getResolution(alertSession)} />
          <DetailRow title={translation.Resolved_By} content={alertSession.resolverName || ''} />
          <DetailRow title={translation.Alert_Source} content={alertSession.initiatorName} />
          <DetailRow title={translation.Participants} content={responderNames.join(', ')} />
          <DetailRow title={translation.Alert_ID} content={alertSession.id} />
        </ul>

        {fingerprintingFlag && isSuperAdmin() && (
          <Toggle css={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
            <DetailRow title={'Fingerprint Mode'} content={''} />
            <FormControlLabel
              control={
                <ToggleSwitch
                  checked={fingerprintMode}
                  onChange={() =>
                    setFingerprintMode(prev => {
                      const next = !prev
                      if (!next) setShowAllFingerprints(false)
                      return next
                    })
                  }
                  name="fingerprintMode"
                />
              }
              label={<SwitchLabel>{fingerprintMode ? 'Enabled' : 'Disabled'}</SwitchLabel>}
            />
          </Toggle>
        )}

        <ul css={detailStyles}>
          <DetailRow title={translation.Last_Location} content={<strong>{lastLocationText}</strong>} />
        </ul>

        {fingerprintMode && (lastBeacons.length || lastFingerprints.length) ? (
          <Box
            style={{
              display: 'flex',
              gap: '1rem',
              padding: '1rem',
              flexWrap: 'wrap',
              maxHeight: '200px',
              overflowY: 'auto',
              position: 'relative',
            }}
          >
            {lastBeacons.length > 0 && (
              <Box style={{ flex: 1 }}>
                <h5 style={{ margin: '0 0 0.5rem 0', fontFamily: 'monospace' }}>📡 Last Beacons</h5>
                <table style={{ width: '60%', fontSize: '1rem', fontFamily: 'monospace' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '4px' }}>Beacon ID</th>
                      <th style={{ textAlign: 'left', padding: '4px' }}>RSSI</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lastBeacons.map((b: any, i: number) => (
                      <tr key={`${b.beaconId}-${i}`}>
                        <td style={{ padding: '2px 4px' }}>{b.beaconId}</td>
                        <td style={{ padding: '2px 4px' }}>{b.rssi}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            )}

            {lastFingerprints.length > 0 && (
              <Box style={{ flex: 1 }}>
                <h5 style={{ margin: '0 0 0.5rem 0', fontFamily: 'monospace' }}>🧬 Last Fingerprints</h5>
                <table style={{ width: '70%', fontSize: '1rem', fontFamily: 'monospace' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '4px' }}>Floor</th>
                      <th style={{ textAlign: 'left', padding: '4px' }}>Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...lastFingerprints]
                      .sort((a, b) => (a.error ?? Infinity) - (b.error ?? Infinity))
                      .map((fp: FingerprintError, i: number) => (
                        <tr key={`${fp.id}-${i}`}>
                          <td style={{ padding: '2px 4px', wordBreak: 'break-word' }}>{fp.floorName}</td>
                          <td style={{ padding: '2px 4px' }}>{fp.error?.toFixed(2)}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </Box>
            )}
          </Box>
        ) : null}

        {fingerprintMode && floors.length > 0 ? (
          <>
            <Box style={{ padding: '0 1rem 1rem 1rem' }}>
              <h5 style={{ margin: '0 0 0.5rem 0', fontFamily: 'monospace' }}>🗂 Select Floor</h5>
              <StyledFormControl>
                <Select
                  id="floor-selector"
                  value={selectedFloor?.id ?? ''}
                  onChange={(e: any) => {
                    const floorId = (e.target.value as string) ?? ''
                    const floor = floors.find((f: any) => f.id === floorId)
                    if (floor) setSelectedFloor(floor)
                  }}
                  IconComponent={KeyboardArrowDownIcon}
                  disableUnderline
                >
                  {[...floors]
                    .sort((a, b) => a.elevationIndex - b.elevationIndex)
                    .map((floor: any) => (
                      <MenuItem key={floor.id} value={floor.id}>
                        {floor.name || `Floor ${floor.id}`}
                      </MenuItem>
                    ))}
                </Select>
              </StyledFormControl>
            </Box>

            <Toggle
              css={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                padding: '0 1rem',
              }}
            >
              <DetailRow
                title={'Show All Fingerprints'}
                content={showAllFingerprints ? 'Click a marker to delete its cluster' : ''}
              />
              <FormControlLabel
                control={
                  <ToggleSwitch
                    checked={showAllFingerprints}
                    onChange={() => setShowAllFingerprints(prev => !prev)}
                    name="showAllFingerprints"
                    disabled={!selectedFloor?.id}
                  />
                }
                label={<SwitchLabel>{showAllFingerprints ? 'Enabled' : 'Disabled'}</SwitchLabel>}
              />
            </Toggle>
            <br />
          </>
        ) : null}

        {utils.getAlertHealth(alertSession) !== HealthLabelsKeys.RESOLVED &&
          alertSession.orgId &&
          (isAdmin(alertSession.orgId) || isDispatcher(alertSession.orgId)) && (
            <div css={actionRowStyles}>
              <ActionButton
                disabled={arrivedDisabled}
                onClick={() => {
                  setMapVisible(false)
                  setShowArrivedDialog(true)
                }}
              >
                {translation.mark_arrived}
              </ActionButton>
              <ActionButton
                onClick={() => {
                  setMapVisible(false)
                  setShowDialog(true)
                }}
              >
                {translation.Resolve_Alert}
              </ActionButton>
            </div>
          )}
      </DetailSliderContent>

      <div css={alertHistoryTitleStyles}>
        <h2 css={detailHeadingStyles}>{translation.alert_history}</h2>
        <div css={alertEventCountStyles}>{`${translation.alert_event_count} (${getEventCount()})`}</div>
        {movingThroughHistory && !scrolledToBottom && (
          <IconButton size="small" title={translation.view_newest} onClick={scrollToBottomHandler}>
            <VerticalAlignBottomIcon fontSize="inherit" />
          </IconButton>
        )}
      </div>

      <Box
        style={{
          flex: 1,
          minHeight: 0,
          backgroundColor: `${GRAY3_COLOR}`,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div ref={chatScrollRef} css={{ ...alertEventsWrapperStyles, flex: 1, minHeight: 0 }} onScroll={handleScroll}>
          {alertEventsLoading ? (
            <LinearProgress css={linearProgressStyles} />
          ) : (
            <AlertChatEvents events={filteredAlertEvents} />
          )}
          {utils.getAlertHealth(alertSession) === HealthLabelsKeys.RESOLVED && (
            <CondensedInfoGadget alertSession={alertSession} title={translation.resolved_info} handleClick={null} />
          )}
          <div ref={chatBottomRef} />
        </div>

        {isActive && shouldShowAddComment && (
          <div css={{ ...alertHistoryTitleStyles, marginTop: 'auto' }}>
            <Input
              id="comment-value"
              disableUnderline
              fullWidth
              placeholder={translation.add_comment}
              value={comment}
              onChange={e => setComment(e.target.value)}
              onKeyDown={(event: React.KeyboardEvent) => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  sendComment()
                }
              }}
              css={{
                color: TEXTFIELD_LABEL_COLOR,
                fontFamily: 'Montserrat',
                fontSize: '1.4rem',
                fontStyle: 'normal',
                fontWeight: 'bold',
                lineHeight: '1.5rem',
                letterSpacing: '0.1rem',
                textAlign: 'left',
                marginBottom: '20px',
              }}
            />
            <ActionButton width="110px" height="28px" onClick={sendComment}>
              {translation.send}
            </ActionButton>
          </div>
        )}

        <ConditionalRender condition={isActive && hasZello}>
          <Box
            style={{
              position: 'sticky',
              bottom: 0,
              backgroundColor: GRAY3_COLOR,
              zIndex: 1,
              display: 'flex',
              justifyContent: 'center',
              padding: '8px 16px 16px',
              width: '100%',
              boxSizing: 'border-box',
            }}
          >
            <div
              style={{
                width: 'clamp(260px, 92%, 720px)',
                margin: '0 auto',
                boxSizing: 'border-box',
              }}
            >
              {isKicked ? (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 10,
                    flexWrap: 'wrap',
                  }}
                >
                  <span style={{ fontSize: 13, color: ERROR_COLOR }}>
                    Another device logged in — voice disconnected.
                  </span>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={reconnect}
                    style={{ borderColor: BRAND_COLOR, color: BRAND_COLOR }}
                  >
                    Reconnect
                  </Button>
                </div>
              ) : (
                <PTTButton
                  width="100%"
                  minWidth={260}
                  height={44}
                  borderRadius={24}
                  title="Push to Talk"
                  disabled={pttDisabled}
                  connecting={connectingUI}
                  sending={zelloState.tx === 'sending'}
                  connectionFailed={alertConnectionFailed}
                  onReconnect={reconnect}
                  lastError={zelloState.error}
                  onStart={handlePttStart}
                  onStop={handlePttStop}
                  disabledTooltip={!voiceAllowed ? voiceGateTooltip : undefined}
                />
              )}
            </div>
          </Box>
        </ConditionalRender>
      </Box>

      <SolveAlertDialog
        props={{
          open: showDialog,
          handleClose: () => {
            setShowDialog(false)
            setMapVisible(true)
          },
          setMapVisible,
          selectedRow: alertSession,
          alertLocation: { value: alertSession.orgId },
          action: ACTION_TYPE.CLOSE,
        }}
      />

      <SolveAlertDialog
        props={{
          open: showArrivedDialog,
          handleClose: () => {
            setShowArrivedDialog(false)
            setMapVisible(true)
          },
          setMapVisible,
          selectedRow: alertSession,
          alertLocation: { value: alertSession.orgId },
          action: ACTION_TYPE.ARRIVED,
        }}
      />
    </Box>
  )
}
